import { createContext, ReactNode, useCallback, useState } from 'react';

import { CommentsTrackingContextValue } from '../types';

/**
 * Context for tracking unread comments state
 */
export const CommentsTrackingContext = createContext<CommentsTrackingContextValue>({
  collaboratorUnreadCommentsIds: [],
  viewerUnreadCommentsIds: [],
  unreadCommentsCount: 0,
  setUnreadCommentsData: () => undefined,
  markCommentsAsRead: () => undefined,
  markAllCommentsAsRead: () => undefined,
});

export interface CommentsTrackingProviderProps {
  children: ReactNode;
  /**
   * Initial collaborator unread comment IDs (optional)
   */
  initialCollaboratorUnreadCommentIds?: string[];
  /**
   * Initial viewer unread comment IDs (optional)
   */
  initialViewerUnreadCommentIds?: string[];
  /**
   * Initial total unread comments count (optional)
   */
  initialUnreadCommentsCount?: number;
}

/**
 * Provider component for comments tracking context
 *
 * @example
 * ```tsx
 * <CommentsTrackingProvider
 *   initialCollaboratorUnreadCommentIds={['1', '2', '3']}
 *   initialViewerUnreadCommentIds={['4', '5']}
 *   initialUnreadCommentsCount={5}
 * >
 *   <YourApp />
 * </CommentsTrackingProvider>
 * ```
 */
export const CommentsTrackingProvider = ({
  children,
  initialCollaboratorUnreadCommentIds = [],
  initialViewerUnreadCommentIds = [],
  initialUnreadCommentsCount = 0,
}: CommentsTrackingProviderProps) => {
  const [collaboratorUnreadCommentIds, setCollaboratorUnreadCommentIds] = useState<string[]>(
    initialCollaboratorUnreadCommentIds,
  );
  const [viewerUnreadCommentIds, setViewerUnreadCommentIds] = useState<string[]>(
    initialViewerUnreadCommentIds,
  );
  const [unreadCommentsCount, setUnreadCommentsCount] = useState<number>(
    initialUnreadCommentsCount,
  );

  /**
   * Set all unread comments data at once
   */
  const setUnreadCommentsData = useCallback(
    (data: {
      collaboratorUnreadCommentsIds: string[];
      viewerUnreadCommentsIds: string[];
      unreadCommentsCount: number;
    }) => {
      setCollaboratorUnreadCommentIds(data.collaboratorUnreadCommentsIds);
      setViewerUnreadCommentIds(data.viewerUnreadCommentsIds);
      setUnreadCommentsCount(data.unreadCommentsCount);
    },
    [],
  );

  /**
   * Mark comments as read by their IDs (works for both collaborator and viewer comments)
   */
  const markCommentsAsRead = useCallback((commentIds: string[]) => {
    const idsToRemove = new Set(commentIds);
    let totalRemovedCount = 0;

    // Remove from collaborator comments
    setCollaboratorUnreadCommentIds((prevIds) => {
      const newIds = prevIds.filter((id) => !idsToRemove.has(id));
      totalRemovedCount += prevIds.length - newIds.length;
      return newIds;
    });

    // Remove from viewer comments
    setViewerUnreadCommentIds((prevIds) => {
      const newIds = prevIds.filter((id) => !idsToRemove.has(id));
      totalRemovedCount += prevIds.length - newIds.length;
      return newIds;
    });

    // Update total count based on how many were actually removed
    if (totalRemovedCount > 0) {
      setUnreadCommentsCount((prev) => Math.max(0, prev - totalRemovedCount));
    }
  }, []);

  /**
   * Mark all comments as read (both collaborator and viewer)
   */
  const markAllCommentsAsRead = useCallback(() => {
    setCollaboratorUnreadCommentIds([]);
    setViewerUnreadCommentIds([]);
    setUnreadCommentsCount(0);
  }, []);

  const contextValue: CommentsTrackingContextValue = {
    collaboratorUnreadCommentsIds: collaboratorUnreadCommentIds,
    viewerUnreadCommentsIds: viewerUnreadCommentIds,
    unreadCommentsCount: unreadCommentsCount,
    setUnreadCommentsData,
    markCommentsAsRead,
    markAllCommentsAsRead,
  };

  return (
    <CommentsTrackingContext.Provider value={contextValue}>
      {children}
    </CommentsTrackingContext.Provider>
  );
};
